import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ChevronRight, Fuel, Gauge, Wrench, Layers, BadgeCheck,
  CheckCircle2, Phone, Mail, Download
} from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';

/* ── 浅色工业风色值 ── */
const ORANGE = 'oklch(0.66 0.19 42)';
const CARD_BG = '#FFFFFF';
const BORDER = '#D0D5E2';
const MUTED_TEXT = '#5A6780';
const DEEP_BG = '#F0F2F5';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

/* ── 技术参数 ── */
const techParams = [
  { test: '拉伸强度', standard: 'ISO 37-2011 / ASTM D412', value: '12 MPa', note: '标准配方实测值' },
  { test: '断裂伸长率', standard: 'ISO 37-2011 / ASTM D412', value: '350%', note: '标准配方实测值' },
  { test: '硬度', standard: 'ISO 48-2010 / ASTM D2240', value: '70±5 Shore A', note: '可定制 45A～85A' },
  { test: '比重', standard: 'ISO 2781-2008', value: '1.35 g/cm³', note: '—' },
  { test: '工作温度范围', standard: '—', value: '-30°C 至 +100°C', note: '间歇接触可至 +120°C' },
  { test: '压缩永久变形率', standard: 'ISO 815 / ASTM D395', value: '≤35%（参考值）', note: '70°C×22h' },
  { test: '耐ASTM 3#油体积变化', standard: 'ISO 1817 / ASTM D471', value: '<20%（参考值）', note: '100°C×70h' },
  { test: '耐磨性（阿克隆）', standard: 'GB/T 1689', value: '≤1.2 cm³/1.61km', note: '标准耐磨配方' },
];

/* ── 核心特性 ── */
const coreFeatures = [
  {
    icon: Fuel,
    title: '卓越耐油性',
    titleEn: 'OIL RESISTANCE',
    desc: '对石油基液压油、润滑油、燃油及动物/植物油脂具有优良耐受性，是机械、液压、汽车传动等含油工况的首选密封材料。',
  },
  {
    icon: Gauge,
    title: '高拉伸强度',
    titleEn: 'HIGH TENSILE STRENGTH',
    desc: '拉伸强度可达 12 MPa 以上，配合高断裂伸长率，在受力复杂的密封和垫片场合中具有优异的机械可靠性。',
  },
  {
    icon: Wrench,
    title: '良好耐磨性',
    titleEn: 'WEAR RESISTANCE',
    desc: '标准配方耐磨性优良，特别是高丙烯腈配方可进一步强化耐磨表现，适合有相对滑动或频繁挤压的密封场合。',
  },
  {
    icon: Layers,
    title: '多种硬度区间',
    titleEn: 'HARDNESS RANGE',
    desc: '可提供 45A 至 85A Shore A 宽硬度区间，软硬配方均可供应，匹配不同压紧力和密封面粗糙度要求。',
  },
  {
    icon: BadgeCheck,
    title: '性价比突出',
    titleEn: 'COST-EFFECTIVE',
    desc: '在耐油密封材料中，NBR 综合性能与成本比最优，是液压与机械领域使用量最大的工业橡胶板材之一。',
  },
];

/* ── 应用场景 ── */
interface AppItem {
  num: string;
  title: string;
  titleEn: string;
  desc: string;
  details: string[];
  img: string | null;
  imgAlt: string;
}

const applications: AppItem[] = [
  {
    num: '①',
    title: '液压与气动系统密封',
    titleEn: 'HYDRAULIC & PNEUMATIC SEALING',
    desc: '液压站、液压缸、阀体及管路接口中，密封件需长期承受高压油液冲刷和往复运动。NBR 对矿物液压油、HF 系列难燃液压液耐受性优良，压缩变形率可控，是液压系统中使用最广的橡胶密封板材。',
    details: ['液压缸端盖密封垫', '阀门密封衬垫', '管路法兰垫片', '液压站底座防渗垫'],
    img: IMAGES.NBR_HYDRAULIC_SEALING,
    imgAlt: '液压系统NBR密封应用',
  },
  {
    num: '②',
    title: '机械设备垫片与衬垫',
    titleEn: 'MECHANICAL GASKETS',
    desc: '工厂机加工设备、重型压力机、车床底座及各类通用机械的接合面密封与减震垫片。NBR 的高拉伸强度与良好耐油性，使其成为机械制造行业中应用最普遍的垫片材料。',
    details: ['机床主轴密封垫', '压力机垫片', '减速箱接合面密封', '泵体法兰密封垫'],
    img: IMAGES.NBR_MECHANICAL_GASKET,
    imgAlt: '机械设备NBR垫片应用',
  },
  {
    num: '③',
    title: '石油天然气开采设备',
    titleEn: 'OIL & GAS EQUIPMENT',
    desc: '油田抽油泵、井口装置、储罐接口及输油管道中，需要与原油、天然气冷凝液及注水介质长期接触的密封组件。NBR 的耐油性和耐压缩变形性能，使其在含油脂工况下稳定工作。',
    details: ['抽油泵阀座密封', '井口防喷器垫片', '储油罐人孔密封', '输油管道接口垫片'],
    img: IMAGES.NBR_OILFIELD_SEALS,
    imgAlt: '石油开采设备NBR密封应用',
  },
  {
    num: '④',
    title: '汽车传动与制动系统',
    titleEn: 'AUTOMOTIVE DRIVETRAIN',
    desc: '变速箱、差速器、制动系统及发动机油底壳密封等含矿物油或齿轮油的部位。NBR 在 -30°C 至 +100°C 区间内对石油基油脂保持良好弹性，是汽车传动系统密封件的常用基础材料。',
    details: ['变速箱接合面密封垫', '差速器密封衬垫', '制动总泵密封垫片', '油底壳接口密封'],
    img: IMAGES.NBR_AUTOMOTIVE_TRANSMISSION,
    imgAlt: '汽车传动系统NBR密封应用',
  },
];

/* ── 丙烯腈含量与性能关系 ── */
const acnGrades = [
  { grade: '低丙烯腈（18%～28%）', oil: '★★★☆☆', lowTemp: '★★★★★', flex: '★★★★★', use: '低温柔性要求高的场合，如寒区油封' },
  { grade: '中丙烯腈（28%～38%）', oil: '★★★★☆', lowTemp: '★★★★☆', flex: '★★★★☆', use: '通用液压与机械密封（最常用）' },
  { grade: '高丙烯腈（38%～50%）', oil: '★★★★★', lowTemp: '★★★☆☆', flex: '★★★☆☆', use: '高耐油要求，如燃油系统密封件' },
];

/* ── 定制能力 ── */
const customFormulas = [
  { dim: '丙烯腈含量', standard: '中等（28%～38%）', custom: '低 / 中 / 高丙烯腈配方可选', use: '平衡耐油性与低温柔性' },
  { dim: '工作温度上限', standard: '+100°C', custom: '可评估至 +120°C（间歇）', use: '较高温油脂接触场合' },
  { dim: '硬度', standard: '70±5 Shore A', custom: '可向 45A 或 85A 调配', use: '软密封面或高压缩场合' },
  { dim: '耐磨强化', standard: '标准配方', custom: '可采用强化耐磨配方', use: '有相对滑动的密封面' },
  { dim: '颜色', standard: '黑色', custom: '可协调特殊颜色（定制）', use: '色彩识别要求场合' },
  { dim: '厚度 / 幅宽', standard: '1.5～20mm / 标准幅宽', custom: '可定制裁切尺寸', use: '大幅板或异形件' },
];

/* ── 产品规格 ── */
const specList = [
  { label: '颜色', value: '黑色（标准）' },
  { label: '厚度范围', value: '1.5mm 至 20.0mm' },
  { label: '幅宽', value: '0.91m 至 2m（标准），可定制' },
  { label: '硬度区间', value: '45A ～ 85A Shore A（可定制）' },
  { label: '丙烯腈含量', value: '低 / 中 / 高含量配方可选' },
  { label: '定制服务', value: '支持裁切、定制尺寸及特殊配方' },
];

export default function ProductNBR() {
  return (
    <div className="bg-background text-foreground min-h-screen">

      {/* ── 面包屑 ── */}
      <div className="border-b border-border" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-xs" style={{ color: MUTED_TEXT }}>
            <Link to="/" className="hover:text-primary transition-colors">首页</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={ROUTE_PATHS.PRODUCTS} className="hover:text-primary transition-colors">产品系列</Link>
            <ChevronRight className="w-3 h-3" />
            <span style={{ color: ORANGE }}>NBR 丁腈橡胶板</span>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden" style={{ background: DEEP_BG, minHeight: 420 }}>
        <img
          src={IMAGES.P12_NBR_SHEET_1_176}
          alt="NBR丁腈橡胶板材"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.12 }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, rgba(15,30,53,0.90) 0%, rgba(15,30,53,0.65) 100%)' }}
        />
        <div className="relative container mx-auto px-4 py-16 lg:py-24">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.div variants={fadeUp} className="mb-8">
              <Link
                to={ROUTE_PATHS.PRODUCTS}
                className="inline-flex items-center gap-2 text-sm transition-colors"
                style={{ color: MUTED_TEXT }}
              >
                <ArrowLeft className="w-4 h-4" />
                返回产品列表
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* 左：文字 */}
              <div>
                <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
                  <span
                    className="px-3 py-1 text-xs font-black tracking-[0.2em] uppercase rounded-sm"
                    style={{ background: 'oklch(0.66 0.19 42 / 0.15)', color: ORANGE, border: `1px solid ${ORANGE}` }}
                  >
                    NBR
                  </span>
                  <span className="text-xs tracking-widest uppercase" style={{ color: MUTED_TEXT }}>
                    NITRILE BUTADIENE RUBBER
                  </span>
                </motion.div>

                <motion.h1 variants={fadeUp} className="text-4xl lg:text-5xl font-black mb-4 leading-tight">
                  <span style={{ color: ORANGE }}>NBR</span> 丁腈橡胶板
                </motion.h1>
                <motion.p variants={fadeUp} className="text-lg font-medium mb-2" style={{ color: 'oklch(0.75 0.010 225)' }}>
                  液压与机械耐油密封的行业标准材料
                </motion.p>
                <motion.p variants={fadeUp} className="text-sm leading-relaxed mb-8" style={{ color: MUTED_TEXT }}>
                  NBR 以卓越的耐石油基油脂性能和高机械强度，成为液压系统、机械设备及汽车传动领域
                  应用量最大的工业橡胶密封板材。所有数据均符合 ISO / ASTM 国际标准体系。
                </motion.p>

                <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                  <Link
                    to={ROUTE_PATHS.CONTACT}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-sm"
                    style={{ background: ORANGE, color: '#FFFFFF' }}
                  >
                    <Phone className="w-4 h-4" />
                    立即询价
                  </Link>
                  <Link
                    to={`${ROUTE_PATHS.TECH_SPECS}#nbr`}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-sm"
                    style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
                  >
                    查看技术参数
                  </Link>
                </motion.div>
              </div>

              {/* 右：产品图 + 快速规格 */}
              <motion.div variants={fadeUp} className="flex flex-col gap-4">
                <div className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                  <img
                    src={IMAGES.P12_NBR_SHEET_1_176}
                    alt="NBR丁腈橡胶板材实物"
                    className="w-full object-cover"
                    style={{ maxHeight: 260, objectPosition: 'center' }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: '工作温度', value: '-30°C ~ +100°C', sub: '间歇可至 +120°C' },
                    { label: '拉伸强度', value: '≥12 MPa', sub: '高于同类通用橡胶' },
                    { label: '板材厚度', value: '1.5 ~ 20.0mm', sub: '幅宽 0.91m ～ 2m' },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className="rounded p-3 text-center"
                      style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                    >
                      <p className="text-[10px] tracking-widest uppercase mb-1" style={{ color: MUTED_TEXT }}>{s.label}</p>
                      <p className="text-sm font-bold leading-tight" style={{ color: ORANGE }}>{s.value}</p>
                      <p className="text-[10px] mt-0.5" style={{ color: MUTED_TEXT }}>{s.sub}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 核心特性 ── */}
      <section className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>CORE PROPERTIES</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">核心特性</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {coreFeatures.map((f) => {
                const Icon = f.icon;
                return (
                  <motion.div
                    key={f.title}
                    variants={fadeUp}
                    className="rounded p-5 flex flex-col gap-3"
                    style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                  >
                    <div
                      className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0"
                      style={{ background: 'oklch(0.66 0.19 42 / 0.12)', border: '1px solid oklch(0.66 0.19 42 / 0.3)' }}
                    >
                      <Icon className="w-5 h-5" style={{ color: ORANGE }} />
                    </div>
                    <div>
                      <p className="text-[9px] font-bold tracking-widest uppercase mb-0.5" style={{ color: MUTED_TEXT }}>{f.titleEn}</p>
                      <p className="text-sm font-bold mb-2">{f.title}</p>
                      <p className="text-xs leading-relaxed" style={{ color: MUTED_TEXT }}>{f.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 技术参数 ── */}
      <section id="tech-params" className="py-16 lg:py-20" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>TECHNICAL DATA</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">NBR 实测物理性能数据</h2>
              <p className="text-sm text-center mt-2" style={{ color: MUTED_TEXT }}>
                所有数值均为标准中丙烯腈配方典型实测值，依据 ISO / ASTM 国际标准体系测试
              </p>
            </motion.div>

            {/* 核心指标 */}
            <motion.div variants={fadeUp} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: '拉伸强度', value: '≥12 MPa', sub: 'ISO 37 / ASTM D412' },
                { label: '断裂伸长率', value: '≥350%', sub: 'ISO 37 / ASTM D412' },
                { label: '耐油体积变化', value: '<20%', sub: 'ASTM 3#油，100°C×70h' },
                { label: '工作温度', value: '-30 ~ +100°C', sub: '中丙烯腈标准配方' },
              ].map((m) => (
                <div
                  key={m.label}
                  className="rounded p-5 text-center"
                  style={{ background: CARD_BG, border: '1px solid oklch(0.66 0.19 42 / 0.25)' }}
                >
                  <p className="text-3xl font-black mb-1" style={{ color: ORANGE }}>{m.value}</p>
                  <p className="text-sm font-semibold mb-1">{m.label}</p>
                  <p className="text-[10px] font-mono" style={{ color: MUTED_TEXT }}>{m.sub}</p>
                </div>
              ))}
            </motion.div>

            {/* 完整参数表 */}
            <motion.div variants={fadeUp} className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                    {['测试项目', '测试标准', '典型数值', '参考说明'].map((h) => (
                      <th key={h} className="px-5 py-3.5 text-left text-xs font-black tracking-widest uppercase" style={{ color: ORANGE }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {techParams.map((row, i) => (
                    <tr
                      key={row.test}
                      style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}
                    >
                      <td className="px-5 py-3.5 font-semibold">{row.test}</td>
                      <td className="px-5 py-3.5 font-mono text-xs" style={{ color: MUTED_TEXT }}>{row.standard}</td>
                      <td className="px-5 py-3.5 font-bold" style={{ color: ORANGE }}>{row.value}</td>
                      <td className="px-5 py-3.5 text-xs" style={{ color: MUTED_TEXT }}>{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
            <motion.div variants={fadeUp} className="mt-5 flex items-start gap-2 text-xs" style={{ color: MUTED_TEXT }}>
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ORANGE }} />
              <span>以上数据为中丙烯腈（28%～38%）标准配方典型实测值。低丙烯腈配方耐低温更佳，高丙烯腈配方耐油性更强，具体选型请联系技术团队确认。</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 丙烯腈含量选型 ── */}
      <section className="py-16" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>ACN CONTENT GUIDE</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">丙烯腈含量与性能关系</h2>
              <p className="text-sm text-center mt-2 max-w-2xl mx-auto" style={{ color: MUTED_TEXT }}>
                NBR 的关键特性由丙烯腈（ACN）含量决定：含量越高，耐油性越强；含量越低，低温柔性越好。
                选型时需根据介质温度与低温使用要求进行权衡。
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                    {['配方类型', '耐油性', '低温柔性', '高温弹性', '推荐应用场合'].map((h, i) => (
                      <th key={h} className="px-5 py-3.5 text-left text-xs font-black tracking-widest uppercase"
                        style={{ color: i === 0 ? ORANGE : 'oklch(0.70 0.015 225)' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {acnGrades.map((row, i) => (
                    <tr key={row.grade} style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}>
                      <td className="px-5 py-3.5 font-semibold">{row.grade}</td>
                      <td className="px-5 py-3.5" style={{ color: i === 2 ? ORANGE : 'oklch(0.78 0.012 225)' }}>{row.oil}</td>
                      <td className="px-5 py-3.5" style={{ color: i === 0 ? ORANGE : 'oklch(0.78 0.012 225)' }}>{row.lowTemp}</td>
                      <td className="px-5 py-3.5 text-xs" style={{ color: MUTED_TEXT }}>{row.flex}</td>
                      <td className="px-5 py-3.5 text-xs" style={{ color: MUTED_TEXT }}>{row.use}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 典型应用场景 ── */}
      <section className="py-16 lg:py-20" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-12">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>NBR APPLICATIONS</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">经过验证的主要应用领域</h2>
            </motion.div>

            <div className="space-y-8">
              {applications.map((app, idx) => (
                <motion.div
                  key={app.num}
                  variants={fadeUp}
                  className="rounded overflow-hidden"
                  style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                >
                  <div className={`grid grid-cols-1 lg:grid-cols-2 ${idx % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                    <div
                      className={`relative overflow-hidden ${idx % 2 === 1 ? 'lg:order-2' : ''}`}
                      style={{ minHeight: 260 }}
                    >
                      {app.img ? (
                        <img
                          src={app.img}
                          alt={app.imgAlt}
                          className="absolute inset-0 w-full h-full object-cover"
                          style={{ opacity: 0.88 }}
                        />
                      ) : (
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center"
                          style={{ background: '#ECEEF2', border: `2px dashed ${BORDER}` }}
                        >
                          <p className="text-xs font-mono" style={{ color: MUTED_TEXT }}>图片位置保留</p>
                        </div>
                      )}
                      <div
                        className="absolute top-4 left-4 w-10 h-10 rounded-sm flex items-center justify-center font-black text-lg z-10"
                        style={{ background: ORANGE, color: '#FFFFFF' }}
                      >
                        {app.num}
                      </div>
                    </div>
                    <div className={`p-8 flex flex-col justify-center ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <p className="text-[10px] font-bold tracking-widest uppercase mb-2" style={{ color: ORANGE }}>{app.titleEn}</p>
                      <h3 className="text-xl font-black mb-4">{app.title}</h3>
                      <p className="text-sm leading-relaxed mb-6" style={{ color: MUTED_TEXT }}>{app.desc}</p>
                      <div>
                        <p className="text-xs font-bold tracking-wider uppercase mb-3" style={{ color: MUTED_TEXT }}>典型用途</p>
                        <div className="grid grid-cols-2 gap-2">
                          {app.details.map((d) => (
                            <div key={d} className="flex items-center gap-2 text-xs">
                              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ORANGE }} />
                              {d}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 定制配方 + 规格 ── */}
      <section className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>CUSTOM FORMULATION & SPECS</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">定制配方能力与产品规格</h2>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              {/* 定制维度表 */}
              <motion.div variants={fadeUp}>
                <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: MUTED_TEXT }}>定制调整维度</h3>
                <div className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                        {['调整维度', '标准值', '可调方向'].map((h) => (
                          <th key={h} className="px-4 py-3 text-left font-black tracking-widest uppercase" style={{ color: ORANGE }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {customFormulas.map((row, i) => (
                        <tr key={row.dim} style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}>
                          <td className="px-4 py-3 font-semibold">{row.dim}</td>
                          <td className="px-4 py-3 font-mono" style={{ color: 'oklch(0.78 0.012 225)' }}>{row.standard}</td>
                          <td className="px-4 py-3 font-bold" style={{ color: ORANGE }}>{row.custom}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>

              {/* 规格 + 附加图 */}
              <motion.div variants={fadeUp} className="flex flex-col gap-6">
                <div className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                  <img
                    src={IMAGES.P12_NBR_SHEET_1_176}
                    alt="NBR丁腈橡胶板材规格展示"
                    className="w-full object-cover"
                    style={{ maxHeight: 200 }}
                  />
                </div>
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: MUTED_TEXT }}>标准产品规格</h3>
                  <div className="space-y-2">
                    {specList.map((spec) => (
                      <div
                        key={spec.label}
                        className="flex items-center gap-4 text-sm py-2.5"
                        style={{ borderBottom: `1px solid ${BORDER}` }}
                      >
                        <span className="w-28 flex-shrink-0 text-xs font-bold tracking-wider uppercase" style={{ color: MUTED_TEXT }}>
                          {spec.label}
                        </span>
                        <span className="font-semibold">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 选型提示 ── */}
      <section className="py-10" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div
              variants={fadeUp}
              className="rounded p-6 flex flex-col md:flex-row gap-5 items-start"
              style={{ background: CARD_BG, border: `1px solid oklch(0.66 0.19 42 / 0.3)` }}
            >
              <div className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background: 'oklch(0.66 0.19 42 / 0.15)', border: `1px solid ${ORANGE}` }}>
                <CheckCircle2 className="w-5 h-5" style={{ color: ORANGE }} />
              </div>
              <div>
                <p className="text-sm font-bold mb-2" style={{ color: ORANGE }}>NBR 选型重要提示</p>
                <p className="text-sm leading-relaxed" style={{ color: MUTED_TEXT }}>
                  NBR 耐油性出色，但<strong className="text-foreground">不耐臭氧、不耐候、不耐酮类和酯类溶剂</strong>。
                  户外暴露或接触极性溶剂场合应选用 EPDM 或 FKM。
                  需同时满足耐高温（150°C+）和耐油要求时，应优先考虑 FKM 氟橡胶。
                  如有疑问，请联系我们的技术团队进行工况评估。
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-14" style={{ background: '#F0F2F5', borderTop: `1px solid ${BORDER}` }}>
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.div variants={fadeUp} className="mb-4">
              <span className="text-xs font-bold tracking-widest uppercase" style={{ color: ORANGE }}>GET A QUOTE</span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-2xl lg:text-3xl font-black mb-4">
              <>需要 NBR 丁腈橡胶板？<br />我们提供技术选型与样品支持</>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-sm mb-8" style={{ color: MUTED_TEXT }}>
              说明您的工况（接触介质类型、温度范围、规格尺寸及丙烯腈含量要求），技术顾问将为您提供选型建议，并可协调实物打样。
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to={ROUTE_PATHS.CONTACT}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-black rounded-sm"
                style={{ background: ORANGE, color: '#FFFFFF' }}
              >
                <Mail className="w-4 h-4" />
                提交询价
              </Link>
              <a
                href="tel:13426031477"
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm"
                style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
              >
                <Phone className="w-4 h-4" />
                13426031477
              </a>
              <Link
                to={ROUTE_PATHS.SELECTION}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm"
                style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
              >
                <Download className="w-4 h-4" />
                查看选型矩阵
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}
